import { CmsProduct } from '../types/cmsProduct';
import {
  TravelPremiumsData,
  TravelQuotesStatus,
} from '../types/travelInsurance';
import { type BlockVertical, NO_VERTICAL } from './moduleMode';
import {
  PersonalLoanCalculatedValue,
  PersonalLoanCalculation,
  resolvePersonalLoanValue,
} from './providers/plCalculator';
import { resolveTiPremiumPrice, TiPremiumPrice } from './providers/tiQuote';
import {
  CalculatedAttributeProvider,
  getCalculatedAttributeProvider,
  getCalculatedAttributeProviderIn,
  isAuthoredElsewhere,
} from './registry';
import type { ScopeCalculations } from './store/types';

export type { PersonalLoanCalculatedValue, PersonalLoanCalculation };

/**
 * Provider data a consumer has at hand. Each provider reads its own slice and
 * ignores the rest; a slice the consumer cannot supply resolves that
 * provider's attributes to `undefined`, never throws.
 *
 * The keys are deliberately REQUIRED with `| undefined` values: adding a
 * provider breaks every manual call site until its author explicitly decides
 * to supply the new slice or acknowledge its absence with `undefined` —
 * a forgotten source is a compile error, not a silently empty cell. Blocks
 * should not build this by hand at all: `useCalculatedAttributeResolver`
 * assembles it from context.
 */
export type CalculationSources = {
  /**
   * The vertical the engine runs the block under; `undefined` where it does not
   * know the block. Known, it decides whether an attribute is calculated here
   * at all (CMS-2052).
   */
  vertical: BlockVertical | undefined;
  travelQuote:
    | {
        premiums?: TravelPremiumsData;
        /** Poll progress — what separates "no price yet" from "no price". */
        status?: TravelQuotesStatus;
        /** The brand's currency, printed when the quote names none. */
        currency?: string;
      }
    | undefined;
  personalLoan:
    | {
        /** Batch results keyed by the product id the request was made with. */
        calculationsByProductId?: Record<string, PersonalLoanCalculation>;
        /**
         * A calculation is in flight; set by a host that shows a loading state
         * meanwhile (the apply form). A page leaves it unset and keeps its
         * previous figures on screen (CMS-1890).
         */
        pending?: boolean;
      }
    | undefined;
};

/**
 * A resolved calculated-attribute value, discriminated by provider. `value`
 * carries the shape of ONE attribute's value for that provider — a TI price
 * with its discount structure, a single PL figure — never the provider's raw
 * batch payload (that is what `CalculationSources` holds). A consumer narrows
 * by `provider` before rendering.
 */
export type CalculatedAttributeValue =
  | { provider: 'TI_QUOTE'; value: TiPremiumPrice }
  | { provider: 'PL_CALCULATOR'; value: PersonalLoanCalculatedValue };

/**
 * Where one calculated attribute stands on one product. "Still coming" and
 * "never coming" render differently, so they are separate states.
 * `undefined` is separate again: not a calculated attribute at all.
 */
export type CalculatedAttributeResult<
  P extends CalculatedAttributeProvider = CalculatedAttributeProvider,
  // Distributive so that `provider === 'TI_QUOTE'` narrows all three states,
  // not just the resolved one.
> = P extends unknown
  ?
      | ({ state: 'resolved' } & Extract<
          CalculatedAttributeValue,
          { provider: P }
        >)
      /** The provider's source has not settled yet; a value may still arrive. */
      | { state: 'pending'; provider: P }
      /**
       * Settled with nothing: no quote matched, no rate band, never requested.
       * Not yet told apart — the fallback contract that needs them is open.
       */
      | { state: 'empty'; provider: P }
      /**
       * Calculated in another vertical only, with nothing authored to fall
       * back on — a travel price in a General table. The surface omits it.
       */
      | { state: 'unavailable'; provider: P }
  : never;

/** The facade's shape, for the places that receive it as a parameter. */
export type CalculatedAttributeResolver = (
  attributeName: string | undefined,
  product: CmsProduct | undefined,
) => CalculatedAttributeResult | undefined;

/**
 * Has a value, or is still waiting for one — as opposed to settled with
 * nothing. What a surface asks when it must stay mounted for the whole wait.
 */
export const hasOrAwaitsValue = (
  result: CalculatedAttributeResult | undefined,
): boolean => result?.state === 'resolved' || result?.state === 'pending';

/**
 * The engine's entry point: resolve a calculated attribute's value for a
 * product from the sources at hand, dispatched by the attribute's provider
 * (see `registry.ts`). `undefined` when the attribute is not calculated, or
 * when its provider cannot produce a value — the consumer decides what an
 * unresolved cell shows (a loader while its source is still loading, its
 * empty state once settled).
 */
export function resolveCalculatedAttribute(
  attributeName: string | undefined,
  product: CmsProduct | undefined,
  sources: CalculationSources,
): CalculatedAttributeResult | undefined {
  const provider = getCalculatedAttributeProvider(attributeName);
  if (!provider) return undefined;

  // Outside the block's vertical the attribute is not calculated: an authored
  // value stands, or there is nothing at all.
  if (
    sources.vertical !== undefined &&
    !getCalculatedAttributeProviderIn(
      attributeName,
      sources.vertical === NO_VERTICAL ? undefined : sources.vertical,
    )
  ) {
    return isAuthoredElsewhere(attributeName)
      ? undefined
      : { state: 'unavailable', provider };
  }

  switch (provider) {
    case 'TI_QUOTE': {
      const value = resolveTiPremiumPrice(
        product,
        sources.travelQuote?.premiums,
        sources.travelQuote?.currency,
      );
      if (value) return { state: 'resolved', provider, value };
      // The one place the poll's progress is turned into an answer.
      return sources.travelQuote?.status === 'inprogress'
        ? { state: 'pending', provider }
        : { state: 'empty', provider };
    }
    case 'PL_CALCULATOR': {
      // The registry matches on the attribute NAME alone, and these nine names
      // are ordinary catalogue attributes elsewhere: a credit-card or mortgage
      // block showing APR has an authored value and never asked for a loan
      // calculation. Answering `empty` there would erase it, so an absent
      // slice means "not calculated here" and the consumer falls back to the
      // catalogue. This is the guard `TableBuilder.isRenderedColumn` states in
      // words for its own mode; the engine now owns it for every consumer.
      // A block that DID ask has a slice — empty for a product outside its
      // rate table — and still gets the dash it should. `pending` is the third
      // case: it says the block asked and the answer is in flight, which is
      // the only way to tell that apart before any figure exists.
      if (
        !sources.personalLoan?.calculationsByProductId &&
        !sources.personalLoan?.pending
      ) {
        return undefined;
      }

      // Figures already in the slot were calculated for the previous terms.
      if (sources.personalLoan?.pending) return { state: 'pending', provider };

      const value = resolvePersonalLoanValue(
        attributeName,
        product,
        sources.personalLoan?.calculationsByProductId,
      );
      return value
        ? { state: 'resolved', provider, value }
        : { state: 'empty', provider };
    }
  }
}

/**
 * The resolver for one block's slot. The hook builds it for blocks, a server
 * surface (the product schema) builds it the same way, so a slot is read one
 * way everywhere and nobody assembles `CalculationSources` by hand.
 */
export const createCalculatedAttributeResolver = (
  slot: ScopeCalculations,
  { currency }: { currency?: string },
): CalculatedAttributeResolver => {
  const sources: CalculationSources = {
    vertical: slot.vertical,
    travelQuote: {
      premiums: slot.travelQuote?.premiums,
      status: slot.travelQuote?.status,
      currency,
    },
    personalLoan: {
      calculationsByProductId: slot.personalLoan?.calculations,
      pending: slot.personalLoan?.pending,
    },
  };

  return (attributeName, product) =>
    resolveCalculatedAttribute(attributeName, product, sources);
};
